import React, { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import PlacesAutocomplete, {
    geocodeByPlaceId,
    getLatLng
} from "react-places-autocomplete";
import firebase from "firebase";
import { useBars, useBarUtils, generateId } from "../actions/bars";
import { useUserRoles } from "../globalstore";
import { auth, firestore, geo } from "../services/firebase";
import supabase from "../services/supabaseClient";
import SignupCard from "../components/SignupCard";

const ROLES = [
    { value: "owner", label: "Owner" },
    { value: "manager", label: "Manager" }
];

export function ClaimBar() {
    const { userRoles } = useUserRoles();
    const barsContext = useBars();
    const { initialFetch, setBarsQuery } = useBarUtils();

    const [searchTerm, setSearchTerm] = useState("");
    const [cantFind, setCantFind] = useState(false);
    const [selectedBar, setSelectedBar] = useState(null);
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [selectedRole, setSelectedRole] = useState("owner");
    const [placesQuery, setPlacesQuery] = useState("");
    const [googlePlace, setGooglePlace] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const { bars = [] } = barsContext || {};

    const currentUser = auth.currentUser;

    const hasRoles = Boolean(userRoles?.roles && Object.keys(userRoles.roles).length > 0);

    useEffect(() => {
        if (!bars.length) {
            initialFetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentUser?.email) {
            setContactEmail((prev) => prev || currentUser.email);
        }
    }, [currentUser?.email]);

    const filteredBars = useMemo(() => {
        if (!Array.isArray(bars)) {
            return [];
        }

        const normalizedBars = bars.map((bar) => {
            if (typeof bar?.data === "function") {
                return { id: bar.id, ...bar.data() };
            }
            return bar;
        });

        if (!searchTerm.trim()) {
            return normalizedBars;
        }

        const term = searchTerm.trim().toLowerCase();
        return normalizedBars.filter((bar) => {
            const name = bar?.barName?.toLowerCase?.() || "";
            const address = bar?.barAddress?.toLowerCase?.() || "";
            return name.includes(term) || address.includes(term);
        });
    }, [bars, searchTerm]);

    const handlePlacesSelect = async (value, placeId) => {
        setError("");
        try {
            const results = await geocodeByPlaceId(placeId);
            const place = results[0];
            const latLng = await getLatLng(place);
            const rawName = place?.name || value;
            const primaryName = rawName?.split(",")[0]?.trim() || rawName;
            const address = place?.formatted_address || value;
            setGooglePlace({
                placeId,
                name: primaryName,
                address,
                lat: latLng?.lat ?? null,
                lng: latLng?.lng ?? null,
                lookupSource: "google"
            });
            setSelectedBar(null);
            setSuccess("");
        } catch (selectError) {
            setError(selectError?.message || "Unable to select that place. Please try again.");
        }
    };

    const resetSelection = () => {
        setSelectedBar(null);
        setGooglePlace(null);
        setSuccess("");
        setError("");
    };

    const handleBarSelect = (entry) => {
        setSelectedBar({
            id: entry.id,
            barName: entry.barName,
            barAddress: entry.barAddress || "",
            lookupSource: "existing"
        });
        setGooglePlace(null);
        setSuccess("");
        setError("");
    };

    const handleSubmit = async () => {
        setError("");
        setSuccess("");

        if (!currentUser) {
            setError("Please sign in before claiming your bar.");
            return;
        }

        let derivedSelectedBar = selectedBar;

        if (!cantFind && !derivedSelectedBar && filteredBars.length > 0) {
            const topMatch = filteredBars[0];
            derivedSelectedBar = {
                id: topMatch.id,
                barName: topMatch.barName,
                barAddress: topMatch.barAddress || "",
                lookupSource: "existing"
            };
            setSelectedBar(derivedSelectedBar);
        }

        const activeSelection = cantFind ? googlePlace : derivedSelectedBar;

        if (!activeSelection) {
            setError("Select your bar before submitting.");
            return;
        }

        if (!contactName.trim() || !contactEmail.trim()) {
            setError("Contact name and email are required.");
            return;
        }

        if (!selectedRole) {
            setError("Select your role at the bar.");
            return;
        }

        setSubmitting(true);

        try {
            let barId = activeSelection.id || null;
            let barName = (activeSelection.barName || activeSelection.name || "").trim();
            let barAddress = (activeSelection.barAddress || activeSelection.address || "").trim();
            let lookupSource =
                activeSelection.lookupSource || (cantFind ? "google" : "existing");
            let googleData = null;

            if (lookupSource === "google") {
                const generatedId = generateId();
                const { lat, lng, name, address } = googlePlace;
                const normalizedName = name ? name.trim() : "";
                const normalizedAddress = address ? address.trim() : "";
                const payload = {
                    bar_id: generatedId,
                    bar_name: normalizedName,
                    bar_name_lower: normalizedName ? normalizedName.toLowerCase() : null,
                    bar_address: normalizedAddress,
                    bar_images: [],
                    bar_cover_image: null,
                    bar_type: null,
                    created_by: currentUser.uid,
                    owner_email: null,
                    geohash:
                        typeof lat === "number" && typeof lng === "number"
                            ? geo.point(Number(lat), Number(lng)).hash
                            : null,
                    location:
                        typeof lat === "number" && typeof lng === "number"
                            ? {
                                  type: "Point",
                                  coordinates: [Number(lng), Number(lat)]
                              }
                            : null,
                    data_source: "claim_google"
                };

                const { error: supabaseError } = await supabase
                    .from("bars")
                    .upsert(payload, { onConflict: "bar_id" });

                if (supabaseError) {
                    throw new Error(
                        supabaseError.message || "Unable to create the bar record right now."
                    );
                }

                const { error: reviewInsertError } = await supabase
                    .from("reviewed_and_contacted_bars")
                    .upsert(
                        {
                            bar_id: generatedId,
                            reviewed_by_admin: false,
                            contacted_by_agent: false
                        },
                        { onConflict: "bar_id", ignoreDuplicates: true }
                    );

                if (reviewInsertError) {
                    throw new Error(
                        reviewInsertError.message ||
                            "Unable to create the review tracking record right now."
                    );
                }

                barId = generatedId;
                barName = normalizedName;
                barAddress = normalizedAddress;
                googleData = {
                    placeId: googlePlace.placeId,
                    lat,
                    lng
                };
            }

            const claimData = {
                userId: currentUser.uid,
                userEmail: currentUser.email?.toLowerCase() || "",
                contactName: contactName.trim(),
                contactEmail: contactEmail.trim().toLowerCase(),
                contactPhone: contactPhone.trim(),
                role: selectedRole,
                barId,
                barName,
                barAddress,
                lookupSource,
                status: "pending",
                createdAt: firebase.firestore.FieldValue.serverTimestamp()
            };

            if (lookupSource === "google") {
                claimData.googlePlace = {
                    ...googleData,
                    name: googlePlace.name,
                    address: googlePlace.address
                };
            }

            await firestore.collection("claimed_bar_review").add(claimData);

            setSuccess(
                "Thanks! An admin will reach out shortly to complete your bar verification."
            );
            setSelectedBar(null);
            setGooglePlace(null);
            setSearchTerm("");
            setPlacesQuery("");
            setContactPhone("");
            setCantFind(false);
        } catch (submitError) {
            setError(
                submitError?.message || "Unable to submit your claim right now."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!currentUser) {
        return (
            <SignupCard
                title="Create your account to claim a bar"
                description="Sign up for BarHopper so we can verify your ownership before granting access."
                onSuccess={() => window.location.reload()}
            />
        );
    }

    return (
        <Container>

            {hasRoles && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You already have bar access. You can still submit another claim if needed.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Section>
                <Typography variant="h6" gutterBottom>
                    Find your bar in BarHopper
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Search for your bar"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setBarsQuery(event.target.value);
                            resetSelection();
                        }}
                        fullWidth
                        disabled={cantFind}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={cantFind}
                                onChange={(event) => {
                                    setCantFind(event.target.checked);
                                    resetSelection();
                                }}
                            />
                        }
                        label="I can't find my bar"
                    />
                    {!cantFind && (
                        <Typography variant="body2" color="textSecondary">
                            We&#39;ll automatically match this name to an existing listing when you submit. Switch on the option labeled I can&#39;t find my bar if you need to share Google search details instead.
                        </Typography>
                    )}
                </Box>

                {!cantFind && searchTerm.trim() && (
                    <BarList>
                        {filteredBars.map((entry) => {
                            const isSelected = selectedBar?.id === entry.id;
                            return (
                                <li key={entry.id}>
                                    <BarButton
                                        onClick={() => handleBarSelect(entry)}
                                        selected={isSelected}
                                    >
                                        <strong>{entry.barName}</strong>
                                        {entry.barAddress && (
                                            <span className="address">{entry.barAddress}</span>
                                        )}
                                    </BarButton>
                                </li>
                            );
                        })}
                        {filteredBars.length === 0 && (
                            <Typography variant="body2" color="textSecondary">
                                No bars matched that search.
                            </Typography>
                        )}
                    </BarList>
                )}

            {cantFind && (
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        Search Google Maps
                    </Typography>
                    <PlacesAutocomplete
                        value={placesQuery}
                        onChange={(value) => {
                            setPlacesQuery(value);
                            setGooglePlace(null);
                        }}
                        onSelect={handlePlacesSelect}
                    >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                            <div>
                                <TextField
                                    {...getInputProps({
                                        placeholder: "Start typing your bar name or address"
                                    })}
                                    fullWidth
                                    label="Google Places search"
                                />
                                <PlacesSuggestions>
                                    {loading && <div>Loading suggestions...</div>}
                                    {suggestions.map((suggestion) => {
                                        const className = suggestion.active ? "active" : "";
                                        return (
                                            <SuggestionOption
                                                key={suggestion.placeId}
                                                {...getSuggestionItemProps(suggestion, {
                                                    className
                                                })}
                                            >
                                                {suggestion.description}
                                            </SuggestionOption>
                                        );
                                    })}
                                </PlacesSuggestions>
                            </div>
                        )}
                    </PlacesAutocomplete>
                    {googlePlace && (
                        <SelectedPlace>
                            <strong>{googlePlace.name}</strong>
                            {googlePlace.address && (
                                <span className="address">{googlePlace.address}</span>
                            )}
                        </SelectedPlace>
                    )}
                </Box>
            )}
            </Section>

            <Section>
                <Typography variant="h6" gutterBottom>
                    Your contact information
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label="Full name"
                        value={contactName}
                        onChange={(event) => setContactName(event.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Email"
                        type="email"
                        value={contactEmail}
                        onChange={(event) => setContactEmail(event.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Phone"
                        value={contactPhone}
                        onChange={(event) => setContactPhone(event.target.value)}
                        fullWidth
                    />
                    <Select
                        value={selectedRole}
                        onChange={(event) => setSelectedRole(event.target.value)}
                        fullWidth
                        displayEmpty
                    >
                        {ROLES.map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Section>

            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    Submit for review
                </Button>
            </Box>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const Section = styled.section`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const suggestionCardStyles = css`
    border-radius: 12px;
    padding: 12px 16px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f8fafc;
    color: #0f172a;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;

const BarList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 12px;
`;

const BarButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "selected"
})`
    && {
        ${suggestionCardStyles};
        width: 100%;
        justify-content: flex-start;
        align-items: flex-start;
        text-transform: none;
        gap: 4px;
        background: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.16)" : "#f8fafc"};
        border-color: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.5)" : "rgba(15, 23, 42, 0.12)"};
        transition: background 0.2s ease, border-color 0.2s ease;
    }

    &&:hover {
        background: ${({ selected }) =>
            selected ? "rgba(25, 118, 210, 0.2)" : "#eef2ff"};
        border-color: rgba(25, 118, 210, 0.5);
    }

    strong {
        font-weight: 600;
    }

    .address {
        font-size: 0.85rem;
        color: rgba(15, 23, 42, 0.6);
    }
`;

const PlacesSuggestions = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SuggestionOption = styled.div`
    ${suggestionCardStyles};
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;

    background: #f8fafc;
    border-color: rgba(15, 23, 42, 0.12);

    &.active {
        background: rgba(25, 118, 210, 0.16);
        border-color: rgba(25, 118, 210, 0.5);
    }
`;

const SelectedPlace = styled.div`
    ${suggestionCardStyles};
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-color: rgba(25, 118, 210, 0.5);
    background: rgba(25, 118, 210, 0.16);
    text-transform: none;

    .label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: rgba(15, 23, 42, 0.6);
    }

    strong {
        font-weight: 600;
    }

    .address {
        font-size: 0.85rem;
        color: rgba(15, 23, 42, 0.6);
    }
`;

export default ClaimBar;
